import React, { useEffect, useState } from 'react';
import healthCheckService, { HealthStatus } from '../services/HealthCheckService';
import logger from '../services/Logger';

const HealthCheck: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        setLoading(true);
        const status = await healthCheckService.getHealthStatus();
        setHealth(status);
        setError(null);
      } catch (err) {
        logger.error('Failed to get health status:', err);
        setError('Failed to retrieve health status');
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div>Loading health status...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!health) {
    return <div>No health data available</div>;
  }

  // Return JSON response for API endpoint
  if (window.location.pathname === '/api/health') {
    return (
      <pre>{JSON.stringify(health, null, 2)}</pre>
    );
  }

  // Return formatted HTML for browser view
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>Health Check Status</h2>
      <div style={{ 
        padding: '10px', 
        backgroundColor: health.status === 'healthy' ? '#d4edda' :
                        health.status === 'degraded' ? '#fff3cd' : '#f8d7da',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <strong>Overall Status:</strong> {health.status.toUpperCase()}
      </div>
      
      <div>
        <strong>Version:</strong> {health.version}<br />
        <strong>Timestamp:</strong> {health.timestamp}<br />
        <strong>Uptime:</strong> {Math.floor(health.uptime / 60)} minutes<br />
        <strong>Environment:</strong> {health.details?.environment || 'N/A'}
      </div>

      <h3>Service Checks</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            <th style={{ textAlign: 'left', padding: '8px' }}>Service</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>Message</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>Response Time</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(health.checks).map(([service, check]) => (
            <tr key={service} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '8px' }}>{service}</td>
              <td style={{ 
                padding: '8px',
                color: check.status === 'pass' ? 'green' :
                       check.status === 'warn' ? 'orange' : 'red'
              }}>
                {check.status.toUpperCase()}
              </td>
              <td style={{ padding: '8px' }}>{check.message || '-'}</td>
              <td style={{ padding: '8px' }}>
                {check.responseTime ? `${check.responseTime.toFixed(2)}ms` : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {health.details && (
        <>
          <h3>Additional Details</h3>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '10px', 
            borderRadius: '5px',
            overflow: 'auto'
          }}>
            {JSON.stringify(health.details, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
};

export default HealthCheck;